import {
  filterLibrary,
  type Bookmark,
  type LibraryCandidate,
  type LibraryFilters,
  type LibraryReceipt,
} from "./domain";

export type SemanticScoreMap = ReadonlyMap<string, number>;

export interface RankedLibraryBookmark {
  candidate: LibraryCandidate;
  score: number;
}

const onlineWeights = {
  semantic: 0.5,
  keywords: 0.25,
  summary: 0.25,
} as const;

const offlineWeights = {
  keywords: 0.5,
  summary: 0.5,
} as const;

/**
 * Accept only the future server boundary promised by #27: card IDs mapped to
 * normalized numeric scores. Raw vectors, excerpts, and search evidence have
 * no representation in this adapter.
 */
export function normalizeSemanticScoreMap(
  input: unknown,
): SemanticScoreMap | null {
  if (input === null || input === undefined) return null;
  if (typeof input !== "object" || Array.isArray(input)) return new Map();

  const scores = new Map<string, number>();
  for (const [rawID, value] of Object.entries(input)) {
    const id = rawID.trim();
    if (!id || typeof value !== "number" || !Number.isFinite(value)) continue;
    scores.set(id, clampUnitInterval(value));
  }
  return scores;
}

export function rankLibraryBookmarks(
  bookmarks: Bookmark[],
  receipts: LibraryReceipt[],
  recipientUserID: string,
  filters: LibraryFilters,
  query: string,
  semanticScores: SemanticScoreMap | null,
): RankedLibraryBookmark[] {
  const eligible = filterLibrary(
    bookmarks,
    receipts,
    recipientUserID,
    filters,
  );
  const queryTokens = normalizedTokens(query);
  if (!queryTokens.length) {
    return eligible.map((candidate) => ({ candidate, score: 0 }));
  }

  return eligible
    .map((candidate, originalIndex) => ({
      candidate,
      originalIndex,
      score: scoreBookmark(candidate.bookmark, queryTokens, semanticScores),
    }))
    .sort((left, right) => (
      right.score - left.score
      || left.originalIndex - right.originalIndex
      || left.candidate.entry.item.id.localeCompare(
        right.candidate.entry.item.id,
      )
    ))
    .map(({ candidate, score }) => ({ candidate, score }));
}

function scoreBookmark(
  bookmark: Bookmark,
  queryTokens: string[],
  semanticScores: SemanticScoreMap | null,
): number {
  const keywordTokens = normalizedTokens(bookmark.keywords.join(" "));
  const summaryTokens = normalizedTokens(bookmark.privateHumanSummary);
  const keywordScore = keywordTokens.length
    ? tokenCoverage(queryTokens, keywordTokens)
    : 0;
  const summaryScore = summaryTokens.length
    ? tokenCoverage(queryTokens, summaryTokens)
    : 0;

  if (semanticScores === null) {
    return (
      keywordScore * offlineWeights.keywords
      + summaryScore * offlineWeights.summary
    );
  }

  const semanticScore = hasCurrentEmbedding(bookmark)
    ? semanticScores.get(bookmark.id) ?? 0
    : 0;
  return (
    semanticScore * onlineWeights.semantic
    + keywordScore * onlineWeights.keywords
    + summaryScore * onlineWeights.summary
  );
}

function hasCurrentEmbedding(bookmark: Bookmark): boolean {
  return (
    bookmark.vectorStatus === "ready"
    && bookmark.embeddedSummaryFingerprint !== null
    && bookmark.embeddedSummaryFingerprint === bookmark.summaryFingerprint
  );
}

function tokenCoverage(queryTokens: string[], candidateTokens: string[]): number {
  const candidates = new Set(candidateTokens);
  const matched = queryTokens.filter((token) => candidates.has(token)).length;
  return matched / queryTokens.length;
}

function normalizedTokens(value: string): string[] {
  return [
    ...new Set(
      value
        .normalize("NFKD")
        .replace(/\p{Mark}+/gu, "")
        .toLocaleLowerCase("en")
        .match(/[\p{Letter}\p{Number}]+/gu) ?? [],
    ),
  ];
}

function clampUnitInterval(value: number): number {
  return Math.min(1, Math.max(0, value));
}
